import { toIntlLocale } from "./dates";

/**
 * Locale-aware money shared by the whole app. Every helper takes the app
 * locale (`es` | `en` | `fr`, as stored by `useLocaleState`; Spanish by
 * default) and the configured currency (EUR by default). Components should use
 * the `useMoneyFormat` hook (`@/hooks/use-money-format`), which binds them.
 */

const toAmount = (value: number | null | undefined): number => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const hasCents = (amount: number): boolean =>
  Math.round(amount * 100) % 100 !== 0;

/**
 * Full amount: "230 €", "0 €", "1.234,50 €" in Spanish, "€230" in English.
 * Whole amounts show no ".00"; amounts with cents show two decimals.
 */
export const formatMoney = (
  value: number | null | undefined,
  locale: string | null | undefined,
  currency: string | null | undefined,
): string => {
  const amount = toAmount(value);
  const decimals = hasCents(amount) ? 2 : 0;
  return new Intl.NumberFormat(toIntlLocale(locale), {
    style: "currency",
    currency: currency || "EUR",
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
};

/**
 * Compact money for tight spots (Kanban column totals): "444,8 mil €" in
 * Spanish, "€444.8k" in English, "444,8 k €" in French. At most one decimal.
 */
export const formatCompactMoney = (
  value: number | null | undefined,
  locale: string | null | undefined,
  currency: string | null | undefined,
): string =>
  new Intl.NumberFormat(toIntlLocale(locale), {
    style: "currency",
    currency: currency || "EUR",
    currencyDisplay: "narrowSymbol",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(toAmount(value));
